import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Button from '../Common/Button';
import ItemDetails from './ItemDetails';
import { EquipmentItem } from '../../types/character';
import './AddToCharacterModal.css';

// Generic item type that can represent both equipment and spells
interface GenericItem {
  id: string;
  name: string;
  description?: string;
  category: string;
  type: string;
  tags?: string[];
  modifiers?: any[];
  // Equipment specific (optional)
  equipped?: boolean;
  // Spell specific (optional)  
  domain?: string;
  level?: number;
  requirements?: string[];
  // Raw data for type-specific properties
  [key: string]: any;
}

interface AddToCharacterModalProps {
  item: EquipmentItem | GenericItem;
  onConfirm: () => void;
  onCancel: () => void;
}

const AddToCharacterModal: React.FC<AddToCharacterModalProps> = ({
  item,
  onConfirm,
  onCancel,
}) => {
  const handleConfirm = () => {
    onConfirm();
  };
  return (
    <motion.div 
      className="add-to-character-modal-overlay" 
      onClick={onCancel}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div 
        className="add-to-character-modal" 
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 50 }}
        transition={{ duration: 0.3, type: "spring", stiffness: 300, damping: 30 }}
      >
        <motion.div 
          className="add-to-character-modal__header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, delay: 0.1 }}
        >
          <h3>Add Item to Character</h3>
          <motion.button
            className="add-to-character-modal__close"
            onClick={onCancel}
            aria-label="Close"
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            transition={{ duration: 0.2 }}
          >
            ✕
          </motion.button>
        </motion.div>

        <motion.div 
          className="add-to-character-modal__content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <ItemDetails
            item={item}
            className="add-to-character-modal__item-details"
          />

          <div className="add-to-character-modal__confirmation">
            <p>
              This item will be added to your character's inventory from the library. 
              You can equip it from the character sheet equipment section.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="add-to-character-modal__actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2, staggerChildren: 0.1 }}
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Button
              variant="SECONDARY"
              size="MEDIUM"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              variant="PRIMARY"
              size="MEDIUM"
              onClick={handleConfirm}
            >
              Add to Character
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default AddToCharacterModal;